function toNum(x) {
  if (x === null || x === undefined) return null;
  if (typeof x === "number") return Number.isFinite(x) ? x : null;
  const s = String(x).trim();
  if (s === "") return null; // important: empty string → null (not 0)
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

export function normalizeLngLat(lngLike, latLike) {
  let lng = toNum(lngLike);
  let lat = toNum(latLike);

  // Fix obvious reversals
  if (lat != null && Math.abs(lat) > 90 && lng != null && Math.abs(lng) <= 90) {
    const t = lng;
    lng = lat;
    lat = t;
  }
  if (lng == null || lat == null) return null;
  if (Math.abs(lng) > 180 || Math.abs(lat) > 90) return null;
  return { lng, lat };
}

/**
 * Extracts and normalizes coordinate points from a segment object.
 *
 * The function looks for coordinates on segment.coordinates (if provided) and supports
 * multiple naming conventions for each point:
 * - Accepts either { longitude, latitude } or { lng, lat } for each named point.
 * - For a "single" (non-transportation / rental) point it also accepts { longitude, latitude }
 *   or { lng, lat } on segment.coordinates directly.
 *
 * Recognized keys on segment.coordinates are: departure, arrival, pickup, return, start, end,
 * and a top-level longitude/latitude pair for a single point. Each found point is passed to
 * normalizeLngLat(longitude, latitude) and the returned normalized value is included in the
 * result; missing points yield null.
 *
 * @param {Object} [segment] - Segment container which may include a `coordinates` object.
 * @param {Object} [segment.coordinates] - Object holding one or more coordinate entries.
 * @returns {Object} An object containing normalized coordinate points (or null when absent).
 *    @property {Array<number>|Object|null} departure Normalized departure coordinates (value returned by normalizeLngLat) or null.
 *    @property {Array<number>|Object|null} arrival Normalized arrival coordinates or null.
 *    @property {Array<number>|Object|null} pickup Normalized pickup coordinates or null.
 *    @property {Array<number>|Object|null} retrn Normalized return coordinates or null. Note: the property name in the result is "retrn".
 *    @property {Array<number>|Object|null} start Normalized start coordinates or null.
 *    @property {Array<number>|Object|null} end Normalized end coordinates or null.
 *    @property {Array<number>|Object|null} single Normalized single (non-transportation/rental) coordinates or null.
 */
export function extractSegmentPoints(segment) {
  const c = segment?.coordinates || {}; // if no coordinates in segment, c = {}

  // if coordinates.departure, normalize departure coords or dep = null
  const dep = c?.departure
    ? normalizeLngLat(
        c.departure.longitude ?? c.departure.lng,
        c.departure.latitude ?? c.departure.lat
      )
    : null;

  // if coordinates.arrival, normalize arrival or arr = null
  const arr = c?.arrival
    ? normalizeLngLat(
        c.arrival.longitude ?? c.arrival.lng,
        c.arrival.latitude ?? c.arrival.lat
      )
    : null;

  // if coordinates.departure, normalize departure coords or dep = null
  const pick = c?.pickup
    ? normalizeLngLat(
        c.pickup.longitude ?? c.pickup.lng,
        c.pickup.latitude ?? c.pickup.lat
      )
    : null;

  // if coordinates.arrival, normalize arrival or arr = null
  const ret = c?.return
    ? normalizeLngLat(
        c.return.longitude ?? c.return.lng,
        c.return.latitude ?? c.return.lat
      )
    : null;

  // activity: if coordinates.start, normalize start coords or start = null
  const start = c?.start
    ? normalizeLngLat(
        c.start.longitude ?? c.start.lng,
        c.start.latitude ?? c.start.lat
      )
    : null;

  // activity: if coordinates.end, normalize end or end = null
  const end = c?.end
    ? normalizeLngLat(c.end.longitude ?? c.end.lng, c.end.latitude ?? c.end.lat)
    : null;

  // accommodation: single coords apply to fixed location segments like accommodation
  const single =
    c.longitude != null && c.latitude != null
      ? normalizeLngLat(c.longitude ?? c.lng, c.latitude ?? c.lat)
      : null;

  return {
    departure: dep,
    arrival: arr,
    pickup: pick,
    retrn: ret,
    start: start,
    end: end,
    single,
  };
}

export function selectPointsForMarker(segment, point = "primary") {
  // point = "primary" || "both"
  const p = String(point || "primary")
    .toLowerCase()
    .trim();
  const { departure, arrival, pickup, retrn, start, end, single } =
    extractSegmentPoints(segment);

  // console.debug(p);
  // transportation
  if (p === "departure")
    return departure ? [{ ...departure, role: "departure" }] : [];
  if (p === "arrival") return arrival ? [{ ...arrival, role: "arrival" }] : [];

  // transportation rental
  if (p === "pickup") return pickup ? [{ ...pickup, role: "pickup" }] : [];
  if (p === "return") return retrn ? [{ ...retrn, role: "return" }] : [];

  // activity
  if (p === "start") return start ? [{ ...start, role: "start" }] : [];
  if (p === "end") return end ? [{ ...end, role: "end" }] : [];

  if (p === "both") {
    const out = [];
    // transportation
    if (departure) out.push({ ...departure, role: "departure" });
    if (arrival) out.push({ ...arrival, role: "arrival" });
    // transportation rental
    if (pickup) out.push({ ...pickup, role: "pickup" });
    if (retrn) out.push({ ...retrn, role: "return" });
    // activity
    if (start) out.push({ ...start, role: "start" });
    if (end) out.push({ ...end, role: "end" });
    return out;
  }

  // transportation rental
  // if (p === "pickup") return pickup ? [{ ...pickup, role: "pickup" }] : [];
  // if (p === "return") return retrn ? [{ ...retrn, role: "return" }] : [];
  // if (p === "both") {
  //   const out = [];
  //   if (pickup) out.push({ ...pickup, role: "pickup" });
  //   if (retrn) out.push({ ...retrn, role: "return" });
  //   return out;
  // }

  // primary
  if (departure) return [{ ...departure, role: "departure" }];
  if (arrival) return [{ ...arrival, role: "arrival" }];
  if (pickup) return [{ ...pickup, role: "pickup" }];
  if (retrn) return [{ ...retrn, role: "return" }];
  if (single) return [{ ...single, role: "location" }];
  return [];
}

/**
 * Generate a stable string key for a geographic coordinate pair.
 *
 * The function accepts an object containing longitude and latitude values
 * (either as numbers or numeric strings) and returns a comma-separated
 * string formatted to the specified number of decimal places: "<lng>,<lat>".
 *
 * Supported input property names:
 * - longitude or lng for the longitude value
 * - latitude or lat for the latitude value
 *
 * If the input is falsy or either coordinate cannot be converted to a finite
 * number, the function returns null.
 *
 * @param {{lng?: number|string, longitude?: number|string, lat?: number|string, latitude?: number|string}|null|undefined} pt
 *   Coordinate object. May be null/undefined to indicate absence of coordinates.
 * @param {number} [precision=6]
 *   Number of decimal places to include for each coordinate (passed to
 *   Number.prototype.toFixed). Should be a non-negative integer; invalid values
 *   may cause a RangeError from toFixed.
 * @returns {string|null}
 *   A string in the form "lng,lat" with each value formatted to the requested
 *   precision, or null if input is missing or coordinates are not finite numbers.
 *
 * @example
 * // default precision (6)
 * coordKey({ lng: -122.4194, lat: 37.7749 });
 * // => "-122.419400,37.774900"
 *
 * @example
 * // using alternate property names and custom precision
 * coordKey({ longitude: "-122.4194", latitude: "37.7749" }, 4);
 * // => "-122.4194,37.7749"
 *
 * @example
 * coordKey(null); // => null
 */
export function coordKey(pt, precision = 6) {
  if (!pt) return null;
  const lng = Number(pt.lng ?? pt.longitude);
  const lat = Number(pt.lat ?? pt.latitude);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
  return `${lng.toFixed(precision)},${lat.toFixed(precision)}`;
}
